import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ReadOnlyProvider } from './context/ReadOnlyContext';
import { PreviewProvider } from './context/PreviewContext';
import { Toaster } from './components/ui/sonner';
import ReadOnlyBanner from './components/ReadOnlyBanner';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import BloodResultsPage from './pages/BloodResultsPage';
import SymptomsPage from './pages/SymptomsPage';
import MedicationsPage from './pages/MedicationsPage';
import TreatmentsPage from './pages/TreatmentsPage';
import GlossaryPage from './pages/GlossaryPage';
import AlertsPage from './pages/AlertsPage';
import ExportPage from './pages/ExportPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import EducationPage from './pages/EducationPage';
import StepByStepBloodEntry from './pages/StepByStepBloodEntry';
import NutritionPage from './pages/NutritionPage';
import StepByStepNutritionEntry from './pages/StepByStepNutritionEntry';
import SupplementsPage from './pages/SupplementsPage';
import VitaminsPage from './pages/VitaminsPage';
import IntegrativeHealthPage from './pages/IntegrativeHealthPage';
import FoodNutritionPage from './pages/FoodNutritionPage';
import RecipesPage from './pages/RecipesPage';
import SupplementsEducationalPage from './pages/SupplementsEducationalPage';
import LifestyleHabitsPage from './pages/LifestyleHabitsPage';
import LearnNotesPage from './pages/LearnNotesPage';
import CommonQuestionsPage from './pages/CommonQuestionsPage';
import HowToUsePage from './pages/HowToUsePage';
import ReadingLearningPage from './pages/ReadingLearningPage';
import UpdatesLearningPage from './pages/UpdatesLearningPage';
import WhatIsCLLPage from './pages/WhatIsCLLPage';
import WatchAndWaitPage from './pages/WatchAndWaitPage';
import BloodMarkersPage from './pages/BloodMarkersPage';
import LearnPage from './pages/LearnPage';
import VitaminDEducationPage from './pages/VitaminDEducationPage';
import StressWellbeingPage from './pages/StressWellbeingPage';
import ExerciseMovementPage from './pages/ExerciseMovementPage';
import SleepRecoveryPage from './pages/SleepRecoveryPage';
import VitaminCIVPage from './pages/VitaminCIVPage';
import AdminRoadmapPage from './pages/AdminRoadmapPage';
import AdminBlogPage from './pages/AdminBlogPage';
import AdminNewsPage from './pages/AdminNewsPage';
import AdminContentCalendarPage from './pages/AdminContentCalendarPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import NewsPage from './pages/NewsPage';
import NewsArticlePage from './pages/NewsArticlePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyIdeasPage from './pages/MyIdeasPage';
import UpgradePage from './pages/UpgradePage';
import SmoothiesPage from './pages/SmoothiesPage';
import EatingWellPage from './pages/EatingWellPage';
import FoodSafetyPage from './pages/FoodSafetyPage';
import CLLDietPage from './pages/CLLDietPage';
import GreenTeaResearchPage from './pages/GreenTeaResearchPage';
import LymphocyteTrendPage from './pages/LymphocyteTrendPage';
import Layout from './components/Layout';
import PreviewLayout from './components/PreviewLayout';
import ScrollToTop from './components/ScrollToTop';
import OnboardingFlow from './components/OnboardingFlow';
// Preview Pages
import PreviewDashboardPage from './pages/preview/PreviewDashboardPage';
import PreviewBloodResultsPage from './pages/preview/PreviewBloodResultsPage';
import PreviewNutritionPage from './pages/preview/PreviewNutritionPage';
import PreviewVitaminsPage from './pages/preview/PreviewVitaminsPage';
import PreviewIntegrativeHealthPage from './pages/preview/PreviewIntegrativeHealthPage';
import PreviewEducationPage from './pages/preview/PreviewEducationPage';
import PreviewVitaminDPage from './pages/preview/PreviewVitaminDPage';
import PreviewLearnNotesPage from './pages/preview/PreviewLearnNotesPage';
import PreviewMyIdeasPage from './pages/preview/PreviewMyIdeasPage';
import PreviewRecipesPage from './pages/preview/PreviewRecipesPage';
import PreviewSmoothiesPage from './pages/preview/PreviewSmoothiesPage';
import PreviewEatingWellPage from './pages/preview/PreviewEatingWellPage';
import PreviewFoodSafetyPage from './pages/preview/PreviewFoodSafetyPage';
import PreviewSymptomsPage from './pages/preview/PreviewSymptomsPage';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <Helmet>
        <title>MyCBC – Blood Cancer Companion</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {children}
    </>
  );
};

// ConditionalLayout - Shows sidebar Layout for logged-in users, plain view for guests
const ConditionalLayout = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  // If logged in, show with sidebar Layout
  if (user) {
    return <Layout>{children}</Layout>;
  }
  
  // If not logged in, show without sidebar
  return children;
};

// AppContent component with onboarding logic
const AppContent = () => {
  const { user, updateUser } = useAuth();
  const [showOnboarding, setShowOnboarding] = React.useState(false);
  const [onboardingChecked, setOnboardingChecked] = React.useState(false);

  // Check if onboarding should show for new users
  React.useEffect(() => {
    if (user && !onboardingChecked) {
      // Only show onboarding if user explicitly has onboarding_completed set to false
      // Existing users (with undefined/null/missing field) are treated as having completed onboarding
      // This ensures only truly new users see the onboarding flow
      const hasCompletedOnboarding = user.onboarding_completed !== false;
      if (!hasCompletedOnboarding) {
        setShowOnboarding(true);
      }
      setOnboardingChecked(true);
    }
  }, [user, onboardingChecked]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Update user state to reflect onboarding completion
    // Set session flag to prevent First-Return experience from showing immediately
    // First-Return should only show after a fresh login (Day 2)
    sessionStorage.setItem('onboarding_just_completed', 'true');
    updateUser({ onboarding_completed: true });
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    // Set session flag even for skipped onboarding
    sessionStorage.setItem('onboarding_just_completed', 'true');
    updateUser({ onboarding_completed: true });
  };

  return (
    <>
      <BrowserRouter>
        <ScrollToTop />
        <ReadOnlyProvider>
          <div className="App">
            <div className="noise-overlay" />
            <ReadOnlyBanner />
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/shop" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/blood-results"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BloodResultsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/symptoms"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SymptomsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/treatments"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TreatmentsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medications"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Navigate to="/treatments" replace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/supplements"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Navigate to="/treatments" replace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vitamins"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Navigate to="/treatments" replace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/glossary"
              element={
                <ProtectedRoute>
                  <Layout>
                    <GlossaryPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AlertsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/news"
              element={
                <ProtectedRoute>
                  <Layout>
                    <NewsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/news/:slug"
              element={
                <ProtectedRoute>
                  <Layout>
                    <NewsArticlePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/export"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExportPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-ideas"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MyIdeasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health"
              element={
                <ProtectedRoute>
                  <Layout>
                    <IntegrativeHealthPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health/food-nutrition"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FoodNutritionPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health/recipes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RecipesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health/supplements"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SupplementsEducationalPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health/lifestyle"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LifestyleHabitsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health/learn-notes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LearnNotesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health/smoothies"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SmoothiesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health/eating-well"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EatingWellPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrative-health/food-safety"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FoodSafetyPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/cll-diet"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CLLDietPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/green-tea-research"
              element={
                <ProtectedRoute>
                  <Layout>
                    <GreenTeaResearchPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/lymphocyte-trend"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LymphocyteTrendPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/education"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EducationPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LearnPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/what-is-cll"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WhatIsCLLPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/watch-and-wait"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WatchAndWaitPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/blood-markers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BloodMarkersPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vitamin-d-education"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VitaminDEducationPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stress-wellbeing"
              element={
                <ProtectedRoute>
                  <Layout>
                    <StressWellbeingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/exercise-movement"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExerciseMovementPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sleep-recovery"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SleepRecoveryPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vitamin-c-iv"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VitaminCIVPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/common-questions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CommonQuestionsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/how-to-use"
              element={
                <ProtectedRoute>
                  <Layout>
                    <HowToUsePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reading-learning"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReadingLearningPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/updates-learning"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UpdatesLearningPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/upgrade"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UpgradePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/nutrition"
              element={
                <ProtectedRoute>
                  <Layout>
                    <NutritionPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/nutrition/add-step-by-step"
              element={
                <ProtectedRoute>
                  <StepByStepNutritionEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blood-results/add-step-by-step"
              element={
                <ProtectedRoute>
                  <StepByStepBloodEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProfilePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/roadmap"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminRoadmapPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminBlogPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/news"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminNewsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/calendar"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminContentCalendarPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/terms"
              element={
                <Layout>
                  <TermsPage />
                </Layout>
              }
            />
            <Route
              path="/privacy"
              element={
                <Layout>
                  <PrivacyPage />
                </Layout>
              }
            />
            
            {/* BLOG ROUTES - Public but show sidebar if logged in */}
            <Route path="/blog" element={<ConditionalLayout><BlogListPage /></ConditionalLayout>} />
            <Route path="/blog/:slug" element={<ConditionalLayout><BlogPostPage /></ConditionalLayout>} />
            
            {/* PREVIEW ROUTES - No auth required, mock data only */}
            <Route
              path="/preview"
              element={<Navigate to="/preview/dashboard" replace />}
            />
            <Route
              path="/preview/dashboard"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewDashboardPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/blood-results"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewBloodResultsPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/nutrition"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewNutritionPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/symptoms"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewSymptomsPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/vitamins"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewVitaminsPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/integrative-health"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewIntegrativeHealthPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/vitamin-d"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewVitaminDPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/education"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewEducationPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/learn-notes"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewLearnNotesPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/my-ideas"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewMyIdeasPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/recipes"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewRecipesPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/smoothies"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewSmoothiesPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/eating-well"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewEatingWellPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
            <Route
              path="/preview/food-safety"
              element={
                <PreviewProvider>
                  <PreviewLayout>
                    <PreviewFoodSafetyPage />
                  </PreviewLayout>
                </PreviewProvider>
              }
            />
          </Routes>
          <Toaster position="top-center" richColors toastOptions={{ style: { marginTop: '47px', padding: '8px 14px' } }} />
          
          {/* Onboarding Flow - Auto-trigger for new users */}
          {showOnboarding && (
            <OnboardingFlow
              isReplay={false}
              onComplete={handleOnboardingComplete}
              onSkip={handleOnboardingSkip}
            />
          )}
        </div>
        </ReadOnlyProvider>
      </BrowserRouter>
    </>
  );
};

// Main App component
function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;